import {analyticsLogger} from 'analytics/analyticsLogger';
import {RefObject, useEffect, useRef, useState} from 'react';

import {HomeViewportProps} from './dashboardAnalytics';
import {TileId} from './dashboardModel';
import {TileImpressionObserver} from './HomeVisitContext';
import {HomeVisitTracker} from './homeVisitTracker';
import {TileContentStatus} from './tileCatalog';

const VISIBILITY_THRESHOLDS = Array.from({length: 11}, (_, i) => i / 10);

export const newHomeVisitId = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

export const readHomeViewport = (effectiveColumns: number): HomeViewportProps => ({
  viewportWidth: window.innerWidth,
  viewportHeight: window.innerHeight,
  effectiveColumns,
});

/**
 * Seen means at least half the tile is on screen, or, for a tile taller than
 * the window, that it fills at least half the window.
 */
export const isTileVisibleEnough = (entry: IntersectionObserverEntry) => {
  if (!entry.isIntersecting) {
    return false;
  }
  const windowHeight = entry.rootBounds?.height ?? window.innerHeight;
  return entry.intersectionRatio >= 0.5 || entry.intersectionRect.height >= windowHeight / 2;
};

const scrollTopOf = (target: EventTarget) =>
  target instanceof Element ? target.scrollTop : window.scrollY;

/**
 * Tracks one home visit: which tiles reach the screen and for how long, how
 * far the person scrolls, and the window size. Returns the observer tiles
 * register with through HomeVisitContext.
 */
export const useHomeVisitTracking = ({
  rootRef,
  visitId,
  order,
  statuses,
  editing,
  effectiveColumns,
}: {
  rootRef: RefObject<HTMLElement>;
  visitId: string;
  order: TileId[];
  statuses: Partial<Record<TileId, TileContentStatus>>;
  editing: boolean;
  effectiveColumns: number;
}) => {
  const [visit, setVisit] = useState<{
    tracker: HomeVisitTracker;
    observer: TileImpressionObserver;
  } | null>(null);
  const effectiveColumnsRef = useRef(effectiveColumns);
  effectiveColumnsRef.current = effectiveColumns;

  useEffect(() => {
    const root = rootRef.current;
    if (!root) {
      return;
    }
    const tracker = new HomeVisitTracker(visitId, {
      pageVisible: document.visibilityState === 'visible',
    });
    tracker.setViewport(readHomeViewport(effectiveColumnsRef.current));

    // Whichever of the window or an ancestor pane scrolls the dashboard, measured
    // from where it sat when home opened.
    let scroller: EventTarget = window;
    let baselineScrollTop = window.scrollY;

    let frame: number | undefined;
    const measure = () => {
      frame = undefined;
      const rect = root.getBoundingClientRect();
      const seenPct = rect.height > 0 ? (window.innerHeight - rect.top) / rect.height : 0;
      tracker.setScroll(
        scrollTopOf(scroller) - baselineScrollTop,
        seenPct,
        Math.round(rect.height)
      );
    };
    const scheduleMeasure = () => {
      frame ??= requestAnimationFrame(measure);
    };
    const onScroll = ({target}: Event) => {
      // Scrolls inside a tile (lists, carousels) don't move the dashboard.
      const isPageScroll = target === document || (target instanceof Node && target.contains(root));
      if (!isPageScroll) {
        return;
      }
      const next = target === document ? window : target;
      if (next && next !== scroller) {
        scroller = next;
        baselineScrollTop = 0;
      }
      scheduleMeasure();
    };

    const tileByElement = new Map<Element, TileId>();
    const visibleElements = new Map<TileId, Set<Element>>();
    const visibleSet = (tileId: TileId) => {
      const set = visibleElements.get(tileId) ?? new Set<Element>();
      visibleElements.set(tileId, set);
      return set;
    };

    const intersections = new IntersectionObserver(
      (entries) => {
        const rootTop = root.getBoundingClientRect().top;
        entries.forEach((entry) => {
          const tileId = tileByElement.get(entry.target);
          if (!tileId) {
            return;
          }
          const visible = visibleSet(tileId);
          if (isTileVisibleEnough(entry)) {
            visible.add(entry.target);
          } else {
            visible.delete(entry.target);
          }
          tracker.setTileVisibility(
            tileId,
            visible.size > 0,
            entry.boundingClientRect.top - rootTop
          );
        });
        scheduleMeasure();
      },
      {threshold: VISIBILITY_THRESHOLDS}
    );

    const observer: TileImpressionObserver = {
      observe: (element, tileId) => {
        tileByElement.set(element, tileId);
        intersections.observe(element);
        return () => {
          intersections.unobserve(element);
          tileByElement.delete(element);
          const visible = visibleSet(tileId);
          visible.delete(element);
          tracker.setTileVisibility(tileId, visible.size > 0);
        };
      },
    };

    // The dashboard grows as tiles load, which moves the share of it on screen.
    const resizes =
      typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(scheduleMeasure);
    resizes?.observe(root);

    const onResize = () => {
      tracker.setViewport(readHomeViewport(effectiveColumnsRef.current));
      scheduleMeasure();
    };
    const onVisibilityChange = () => tracker.setPageVisible(document.visibilityState === 'visible');
    const onPageHide = () => {
      tracker.end('pagehide');
      analyticsLogger().flushOnPageHide();
    };

    document.addEventListener('scroll', onScroll, {capture: true, passive: true});
    window.addEventListener('resize', onResize);
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', onPageHide);
    setVisit({tracker, observer});

    return () => {
      document.removeEventListener('scroll', onScroll, {capture: true});
      window.removeEventListener('resize', onResize);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', onPageHide);
      if (frame !== undefined) {
        cancelAnimationFrame(frame);
      }
      resizes?.disconnect();
      intersections.disconnect();
      tracker.end('navigate');
    };
  }, [rootRef, visitId]);

  useEffect(() => visit?.tracker.setOrder(order), [visit, order]);
  useEffect(() => visit?.tracker.setContentStatuses(statuses), [visit, statuses]);
  useEffect(() => visit?.tracker.setEditing(editing), [visit, editing]);
  useEffect(
    () => visit?.tracker.setViewport(readHomeViewport(effectiveColumns)),
    [visit, effectiveColumns]
  );

  return visit?.observer ?? null;
};
